import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

import {
  ACCEPTED_FILES_CSV,
  ACCEPTED_FILES_EXCEL,
  ACCEPTED_FILES_IMAGE,
  ACCEPTED_FILES_PDF,
  ACCEPTED_FILES_POWERPOINT,
  ACCEPTED_FILES_WORD,
  ACCEPTED_FILES_ZIP
} from '@/constants/common';
import { TICKET_STATUS } from '@/pages/Tickets/Tickets.constants';

import type { TicketDialog } from './Ticket.types';

dayjs.extend(utc);
export const EMPTY_TICKET_DIALOG: TicketDialog = {
  assignee_id: undefined,
  attachments: [],
  custom_data: {},
  customer_ticket_id: '',
  external_ticket_id: '',
  followup_date: dayjs()
    .utc()
    .set('hour', 21)
    .set('minute', 0)
    .set('second', 0)
    .add(1, 'day'),
  next_action: null,
  organization_id: -1,
  owner_id: -1,
  summary: '',
  tags: [],
  team_queue: '',
  ticket_category: '',
  ticket_priority: -1,
  ticket_subcategory: null,
  ticket_type: 2,
  ticket_watchers: [],
  title: ''
};

export const EMPTY_TICKET_PAGE = {
  id: '',
  activity: [],
  alert_source: null,
  assignee: null,
  attachments: [],
  detected_at: null,
  external_ticket_id: '',
  customer_ticket_id: '',
  investigate_url: '',
  followup_date: dayjs()
    .utc()
    .set('hour', 21)
    .set('minute', 0)
    .set('second', 0)
    .add(1, 'day'),
  ip_address: '',
  last_updated_at: '',
  messages: [],
  next_action: null,
  organization: '',
  owner: '',
  published_at: '',
  summary: '',
  turbine_record_id: '',
  team_queue: '',
  ticket_category: '',
  ticket_external_threats: [],
  ticket_priority: '',
  ticket_recommendations: [],
  ticket_status: '',
  ticket_type: 1,
  ticket_watchers: [],
  title: '',
  time_to_acknowledge: 0,
  time_to_notify: 0,
  time_to_remediate: 0,
  time_to_detect: 0
};

export const EMPTY_TICKET_REFERENCE = {
  checked: false,
  new_ticket_id: -1,
  original_ticket_id: -1
};

export const EMPTY_TICKET_TEMPLATE = {
  category: 0,
  owner_id: -1,
  subcategory: 1,
  summary: '',
  team_queue: '',
  title: ''
};

export const TICKET_ATTACHMENT_ACCEPTED_FILE_TYPES = [
  ...ACCEPTED_FILES_CSV,
  ...ACCEPTED_FILES_EXCEL,
  ...ACCEPTED_FILES_IMAGE,
  ...ACCEPTED_FILES_PDF,
  ...ACCEPTED_FILES_POWERPOINT,
  ...ACCEPTED_FILES_WORD,
  ...ACCEPTED_FILES_ZIP
];

export enum TICKET_THREAT_RESOLUTION_ID {
  THREAT_MITIGATED = 1,
  FALSE_POSITIVE = 2,
  BENIGN_ACTIVITY = 3,
  INCONCLUSIVE = 4,
  DUPLICATE_TICKET = 5,
  NO_RESPONSE = 6,
  ESCALATED_TO_DFIR = 7
}

export const TICKET_THREAT_RESOLUTIONS = [
  {
    id: TICKET_THREAT_RESOLUTION_ID.THREAT_MITIGATED,
    sub_text:
      'The threat has been successfully mitigated and no longer poses a risk.',
    text: 'Threat Mitigated'
  },
  {
    id: TICKET_THREAT_RESOLUTION_ID.FALSE_POSITIVE,
    sub_text:
      'The alert was triggered in error and does not represent a real threat.  No further monitoring or alerting is needed for this type of activity.',
    text: 'False Positive'
  },
  {
    id: TICKET_THREAT_RESOLUTION_ID.BENIGN_ACTIVITY,
    sub_text:
      'The behavior is legitimate and not harmful but may still warrant visibility or ongoing alerting due to its relevance in the environment.',
    text: 'Benign Activity'
  },
  {
    id: TICKET_THREAT_RESOLUTION_ID.INCONCLUSIVE,
    sub_text:
      'The ticket is closed without further action, possibly due to a lack of sufficient information to proceed.',
    text: 'Inconclusive'
  },
  {
    id: TICKET_THREAT_RESOLUTION_ID.DUPLICATE_TICKET,
    sub_text:
      'The ticket was found to be a duplicate of another existing ticket and has been closed.',
    text: 'Duplicate Ticket'
  },
  {
    id: TICKET_THREAT_RESOLUTION_ID.NO_RESPONSE,
    sub_text: 'Ticket was closed by Pondurance SOC due to lack of response.',
    text: 'No Response'
  },
  {
    id: TICKET_THREAT_RESOLUTION_ID.ESCALATED_TO_DFIR,
    sub_text:
      'All further communications will be handled via the DFIR engagement.',
    text: 'Escalated to DFIR'
  }
];

export const TICKET_FEEDBACK_REASONS = [
  {
    id: 1,
    text: 'Not timely'
  },
  {
    id: 2,
    text: 'Incomplete information'
  },
  {
    id: 3,
    text: 'Lacking context'
  },
  {
    id: 4,
    text: 'Missing correlation with other similar events'
  },
  {
    id: 5,
    text: 'Other'
  }
];

export const TICKET_THREAT_RESOLUTIONS_MAP = Object.fromEntries(
  TICKET_THREAT_RESOLUTIONS.map((obj) => [`${obj.id}`, obj.text])
) as Record<string, string>;

// ticket status buttons
export const TICKET_STATUS_BUTTON_ACKNOWLEDGE = {
  label: 'Acknowledge',
  newStatusName: TICKET_STATUS.ACKNOWLEDGED
};
export const TICKET_STATUS_BUTTON_NONE = {
  label: '',
  newStatusName: ''
};
export const TICKET_STATUS_BUTTON_CLOSE = {
  label: 'Close',
  newStatusName: TICKET_STATUS.COMPLETED
};
export const TICKET_STATUS_BUTTON_PUBLISH = {
  label: 'Publish',
  newStatusName: TICKET_STATUS.OPEN
};
export const TICKET_STATUS_BUTTON_REMEDIATE = {
  label: 'Remediate',
  newStatusName: TICKET_STATUS.REMEDIATED
};
export const TICKET_STATUS_BUTTON_REOPEN = {
  label: 'Reopen',
  newStatusName: TICKET_STATUS.OPEN
};
export const TICKET_STATUS_BUTTON_START_REVIEW = {
  label: 'Start Review',
  newStatusName: TICKET_STATUS.NEEDS_REVIEW
};

export const DEFAULT_ORG_WATCHER = {
  disable: false,
  displayIcon: false,
  iconTooltipTitle: '',
  id: 0,
  internal: true,
  label: 'Default Organization Watchers',
  notificationAnnouncement: true,
  notificationTicketService: true,
  notificationThreatPriority: [1, 2, 3, 4, 5],
  notificationTicketThreat: true,
  notificationTags: [],
  subLabel: 'All default users in the organization',
  userId: 0,
  value: '0',
  organizationIds: []
};

export enum TICKET_TYPE {
  SERVICE = 2,
  THREAT = 1
}

export const AI_SOC_TEAM_QUEUE = 'AI SOC';

/* regexes */
// double backslash tags = \\<tag>text</tag>
export const DOUBLE_BACKSLASH_TAGS_REGEX = /(\\<|\\<\/)(?![^>]*\/>)[^>]*>/g;
// tags = <tag>text</tag>
export const TAGS_REGEX = /(<|<\/)(?![^>]*\/>)[^>]*>/g;

/* url regexes */
// the markdown editor escapes ASCII punctuation in urls with a backslash (e.g. \= \& \_ \~ \[), which must be removed before saving
export const MARKDOWN_ESCAPED_PUNCTUATION_REGEX = /\\([!-/:-@[-`{-~])/g;

export const URL_REGEX =
  /\bhttps?:\/\/(www\.)?(docs.pondurance.com|events.pondurance.com|scope.pondurance.com|pondurance.auth0.com|pondurance.outsystemsenterprise.com|pondurance.com)[^\s]*/gi;
export const LOGSCALE_URL_REGEX =
  /\bhttps?:\/\/(www\.)?events.pondurance.com[^\s]*/gi;
